import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Loader2, CalendarIcon, Upload, Plus, Video, Radio, FileQuestion, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface Category {
  id: string;
  name: string;
  slug: string;
}

const courseTypes = [
  { value: 'video', label: 'Video Corso', icon: Video, description: 'Corso con video on-demand' },
  { value: 'live', label: 'Live Corso', icon: Radio, description: 'Corso con sessioni live' },
  { value: 'quiz', label: 'Quiz/Test', icon: FileQuestion, description: 'Corso basato su quiz e test' },
  { value: 'mixed', label: 'Misto', icon: Layers, description: 'Combinazione di video, live e quiz' },
];

export default function CreateCoursePage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [showNewCategoryDialog, setShowNewCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    price: '',
    category_id: '',
    course_type: 'video',
    level: 'Principiante',
    language: 'Italiano',
    max_students: '',
    start_date: undefined as Date | undefined,
    end_date: undefined as Date | undefined,
    video_intro_url: '',
    max_participants: '',
    quiz_passing_score: '',
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    const { data } = await supabase
      .from('categories')
      .select('*')
      .order('name', { ascending: true });

    if (data) {
      setCategories(data);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'title' && !formData.slug ? {
        slug: value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      } : {})
    }));
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadThumbnail = async (courseId: string): Promise<string | null> => {
    if (!thumbnailFile) return null;

    const fileExt = thumbnailFile.name.split('.').pop();
    const fileName = `${courseId}-${Date.now()}.${fileExt}`;
    const filePath = `${profile?.id}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('course-thumbnails')
      .upload(filePath, thumbnailFile);

    if (uploadError) {
      console.error('Upload error:', uploadError);
      return null;
    }

    const { data } = supabase.storage
      .from('course-thumbnails')
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const handleCreateNewCategory = async () => {
    if (!newCategoryName.trim()) return;

    const slug = newCategoryName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

    const { data, error } = await supabase
      .from('categories')
      .insert({ name: newCategoryName, slug })
      .select()
      .single();

    if (data && !error) {
      setCategories(prev => [...prev, data]);
      setFormData(prev => ({ ...prev, category_id: data.id }));
      setNewCategoryName('');
      setShowNewCategoryDialog(false);
      toast({
        title: 'Categoria creata',
        description: 'La nuova categoria è stata aggiunta con successo!',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: course, error } = await supabase
        .from('courses')
        .insert({
          teacher_id: profile?.id,
          title: formData.title,
          slug: formData.slug,
          description: formData.description,
          price: parseFloat(formData.price) || 0,
          category_id: formData.category_id || null,
          category: formData.category_id ? undefined : 'Generale',
          course_type: formData.course_type,
          level: formData.level,
          language: formData.language,
          max_students: formData.max_students ? parseInt(formData.max_students) : null,
          start_date: formData.start_date?.toISOString(),
          end_date: formData.end_date?.toISOString(),
          is_published: false,
        })
        .select()
        .single();

      if (error) throw error;

      if (thumbnailFile && course) {
        const thumbnailUrl = await uploadThumbnail(course.id);
        if (thumbnailUrl) {
          await supabase
            .from('courses')
            .update({ thumbnail_url: thumbnailUrl })
            .eq('id', course.id);
        }
      }

      toast({
        title: 'Corso creato',
        description: 'Il tuo corso è stato creato con successo!',
      });

      navigate(`/dashboard/my-courses/${course.id}/edit`);
    } catch (error: any) {
      toast({
        title: 'Errore',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (profile?.role !== 'docente') {
    navigate('/dashboard');
    return null;
  }

  const selectedCourseType = courseTypes.find(t => t.value === formData.course_type);

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6 pb-8">
        <div>
          <Link to="/dashboard/my-courses">
            <Button variant="ghost" size="sm" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna ai corsi
            </Button>
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Crea nuovo corso
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Inizia a creare il tuo corso online
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informazioni di base</CardTitle>
              <CardDescription>
                Inserisci le informazioni principali del corso
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Titolo del corso *</Label>
                <Input
                  id="title"
                  name="title"
                  placeholder="Es: Introduzione a React"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">URL del corso *</Label>
                <Input
                  id="slug"
                  name="slug"
                  placeholder="introduzione-a-react"
                  value={formData.slug}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                <p className="text-xs text-gray-500">
                  Questo sarà l'URL del tuo corso
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrizione *</Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder="Descrivi il tuo corso..."
                  rows={5}
                  value={formData.description}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Categoria *</Label>
                  <div className="flex gap-2">
                    <Select
                      value={formData.category_id}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, category_id: value }))}
                      required
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Seleziona categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Dialog open={showNewCategoryDialog} onOpenChange={setShowNewCategoryDialog}>
                      <DialogTrigger asChild>
                        <Button type="button" variant="outline" size="icon">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Crea nuova categoria</DialogTitle>
                          <DialogDescription>
                            Inserisci il nome della nuova categoria
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <Input
                            placeholder="Nome categoria"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                          />
                          <Button onClick={handleCreateNewCategory} className="w-full">
                            Crea categoria
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price">Prezzo (€) *</Label>
                  <Input
                    id="price"
                    name="price"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="99.00"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>Livello *</Label>
                  <Select
                    value={formData.level}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, level: value }))}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Principiante">Principiante</SelectItem>
                      <SelectItem value="Intermedio">Intermedio</SelectItem>
                      <SelectItem value="Avanzato">Avanzato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="language">Lingua *</Label>
                  <Select
                    value={formData.language}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, language: value }))}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Italiano">Italiano</SelectItem>
                      <SelectItem value="Inglese">Inglese</SelectItem>
                      <SelectItem value="Spagnolo">Spagnolo</SelectItem>
                      <SelectItem value="Francese">Francese</SelectItem>
                      <SelectItem value="Tedesco">Tedesco</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max_students">Studenti massimi</Label>
                  <Input
                    id="max_students"
                    name="max_students"
                    type="number"
                    min="1"
                    placeholder="Illimitato"
                    value={formData.max_students}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500">Lascia vuoto per nessun limite</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tipologia e date</CardTitle>
              <CardDescription>
                Scegli il tipo di corso e le date
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>Tipologia corso *</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {courseTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, course_type: type.value }))}
                        className={cn(
                          'flex items-start gap-3 p-4 border-2 rounded-lg text-left transition-all',
                          formData.course_type === type.value
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        )}
                      >
                        <Icon className={cn(
                          'w-6 h-6 flex-shrink-0 mt-0.5',
                          formData.course_type === type.value ? 'text-blue-600' : 'text-gray-400'
                        )} />
                        <div className="flex-1">
                          <div className="font-semibold text-sm">{type.label}</div>
                          <div className="text-xs text-gray-600 mt-1">{type.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Data inizio</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !formData.start_date && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.start_date ? (
                          format(formData.start_date, 'PPP', { locale: it })
                        ) : (
                          'Seleziona data'
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.start_date}
                        onSelect={(date) => setFormData(prev => ({ ...prev, start_date: date }))}
                        locale={it}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Data fine</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !formData.end_date && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.end_date ? (
                          format(formData.end_date, 'PPP', { locale: it })
                        ) : (
                          'Seleziona data'
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.end_date}
                        onSelect={(date) => setFormData(prev => ({ ...prev, end_date: date }))}
                        locale={it}
                        disabled={(date) =>
                          formData.start_date ? date < formData.start_date : false
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Immagine di copertina</CardTitle>
              <CardDescription>
                Carica un'immagine per il tuo corso
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col items-center gap-4">
                {thumbnailPreview ? (
                  <div className="relative w-full max-w-md aspect-video rounded-lg overflow-hidden border-2 border-gray-200">
                    <img
                      src={thumbnailPreview}
                      alt="Anteprima"
                      className="w-full h-full object-cover"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => {
                        setThumbnailFile(null);
                        setThumbnailPreview(null);
                      }}
                    >
                      Rimuovi
                    </Button>
                  </div>
                ) : (
                  <label className="w-full max-w-md aspect-video border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-colors">
                    <Upload className="w-12 h-12 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">Clicca per caricare</span>
                    <span className="text-xs text-gray-500 mt-1">PNG, JPG fino a 5MB</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </CardContent>
          </Card>

          {formData.course_type === 'video' && (
            <Card>
              <CardHeader>
                <CardTitle>Impostazioni Video Corso</CardTitle>
                <CardDescription>
                  Configurazione per corso basato su video
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="video_intro_url">URL video introduttivo (opzionale)</Label>
                  <Input
                    id="video_intro_url"
                    name="video_intro_url"
                    placeholder="https://..."
                    value={formData.video_intro_url}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500">
                    Potrai aggiungere più video nelle lezioni dopo aver creato il corso
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {formData.course_type === 'live' && (
            <Card>
              <CardHeader>
                <CardTitle>Impostazioni Live Corso</CardTitle>
                <CardDescription>
                  Configurazione per corso con sessioni live
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="max_participants">Numero massimo partecipanti</Label>
                  <Input
                    id="max_participants"
                    name="max_participants"
                    type="number"
                    min="1"
                    placeholder="50"
                    value={formData.max_participants}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500">
                    Potrai programmare le live sessions dopo aver creato il corso
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {formData.course_type === 'quiz' && (
            <Card>
              <CardHeader>
                <CardTitle>Impostazioni Quiz</CardTitle>
                <CardDescription>
                  Configurazione per corso basato su quiz
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="quiz_passing_score">Punteggio minimo per superare (%)</Label>
                  <Input
                    id="quiz_passing_score"
                    name="quiz_passing_score"
                    type="number"
                    min="0"
                    max="100"
                    placeholder="70"
                    value={formData.quiz_passing_score}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500">
                    Potrai creare i quiz dopo aver creato il corso
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {formData.course_type === 'mixed' && (
            <Card>
              <CardHeader>
                <CardTitle>Impostazioni Corso Misto</CardTitle>
                <CardDescription>
                  Combinazione di video, live e quiz
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">
                  Potrai aggiungere video, programmare live sessions e creare quiz dopo aver creato il corso
                </p>
              </CardContent>
            </Card>
          )}

          <div className="flex gap-3 pt-4">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creazione...
                </>
              ) : (
                'Crea corso'
              )}
            </Button>
            <Link to="/dashboard/my-courses">
              <Button type="button" variant="outline" disabled={loading}>
                Annulla
              </Button>
            </Link>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
